import type { AuthMode } from "@/components/auth/authMode";
import { DEFAULT_TRIAL_DURATION_DAYS } from "@shared/constants/trial";
import asrLogoChalk from "@assets/logo/ASR-Primary-Chalk.webp";

import { Link } from "wouter";

export interface AuthMobileBrandHeaderProps {
  mode: AuthMode;
  hasPendingInvite?: boolean;
}

function getMobileCopy(
  mode: AuthMode,
  hasPendingInvite = false,
): {
  headline: string;
  headlineBreak: string;
  prompt: string;
  linkLabel: string;
  linkHref: string;
} {
  if (mode === "sign-in") {
    return {
      headline: "Safety and compliance,",
      headlineBreak: "made simple.",
      prompt: "New to A Safer Risk?",
      linkLabel: "Start your free trial",
      linkHref: "/free-trial",
    };
  }

  if (mode === "free-trial") {
    return {
      headline: `Your ${DEFAULT_TRIAL_DURATION_DAYS}-day journey`,
      headlineBreak: "starts here.",
      prompt: "Already have an account?",
      linkLabel: "Sign in",
      linkHref: "/login",
    };
  }

  if (mode === "recovery") {
    if (hasPendingInvite) {
      return {
        headline: "You're invited.",
        headlineBreak: "",
        prompt: "Signed in with the wrong account?",
        linkLabel: "Sign out",
        linkHref: "/logout",
      };
    }

    return {
      headline: "You're almost",
      headlineBreak: "connected.",
      prompt: "Signed in with the wrong account?",
      linkLabel: "Sign out",
      linkHref: "/logout",
    };
  }

  return {
    headline: "Safety and compliance,",
    headlineBreak: "made simple.",
    prompt: "Already have an account?",
    linkLabel: "Sign in",
    linkHref: "/login",
  };
}

/**
 * Full-bleed brand strip for auth screens below the md breakpoint.
 * Desktop branding lives in AuthBrandPanel instead.
 */
export function AuthMobileBrandHeader({
  mode,
  hasPendingInvite = false,
}: AuthMobileBrandHeaderProps) {
  const { headline, headlineBreak, prompt, linkLabel, linkHref } = getMobileCopy(
    mode,
    hasPendingInvite,
  );

  return (
    <header className="bg-brand-midnight px-6 pb-8 pt-10 md:hidden">
      <img
        src={asrLogoChalk}
        alt="A Safer Risk"
        className="w-36"
        width={144}
        height={45}
      />
      <h1 className="mt-6 text-2xl font-semibold leading-tight tracking-tight text-white">
        {headlineBreak ? (
          <>
            {headline}
            <br />
            {headlineBreak}
          </>
        ) : (
          headline
        )}
      </h1>
      <p className="mt-4 text-sm text-white/70">
        {prompt}{" "}
        <Link
          href={linkHref}
          className="font-medium text-brand-teal underline-offset-4 hover:underline"
        >
          {linkLabel}
        </Link>
      </p>
    </header>
  );
}
